import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth';
import { PortalAuthProvider, usePortalAuth } from '@/lib/portalAuth';
import { BrandingProvider } from '@/lib/branding';
import { QueryProvider } from '@/lib/queryProvider';
import { Toaster } from '@/components/ui/sonner';
import { AppDialogProvider } from '@/components/ui/AppDialogProvider';
import { AppShell } from '@/components/layout/AppShell';
import { PortalShell } from '@/components/layout/PortalShell';
import { ForceChangePasswordModal } from '@/components/shared/ForceChangePassword';

// Pages
import LoginPage from '@/pages/LoginPage';
import DeleteAccountPage from '@/pages/public/DeleteAccountPage';
import Dashboard from '@/pages/Dashboard';
import StaffManagement from '@/pages/admin/StaffManagement';
import RolesPage from '@/pages/admin/RolesPage';
import CarePlanTemplates from '@/pages/admin/CarePlanTemplates';
import OrgSettingsPage from '@/pages/admin/OrgSettingsPage';
import StaffListPage from '@/pages/staff/StaffListPage';
import StaffDetailPage from '@/pages/staff/StaffDetailPage';
import StaffAvailabilityViewerPage from '@/pages/workforce/StaffAvailabilityViewerPage';
import ParticipantListPage from '@/pages/participants/ParticipantListPage';
import ParticipantDetailPage from '@/pages/participants/ParticipantDetailPage';
import RosterAdmin from '@/pages/admin/RosterAdmin';
import TimesheetApproval from '@/pages/admin/TimesheetApproval';
import TimesheetApprovalV2Page from '@/pages/timesheets/TimesheetApprovalV2Page';
import Invoices from '@/pages/admin/Invoices';
import PortalRosterPage from '@/pages/portal/PortalRosterPage';
import ShiftDetail from '@/pages/staff/ShiftDetail';
import MyRoster from '@/pages/staff/MyRoster';
import OpenShifts from '@/pages/staff/OpenShifts';
import MyWorkPage from '@/pages/staff/MyWorkPage';
// Staff Portal v2 — flag-gated route wrappers. Wrapper components decide
// whether to render the legacy page or the v2 shell + v2 content.
import StaffDashboardRoute from '@/pages/staff/StaffDashboardRoute';
import MyWorkRoute from '@/pages/staff/MyWorkRoute';
import StaffPortalRoute from '@/components/staff-v2/StaffPortalRoute';
import MySwapRequestsPage from '@/pages/staff/MySwapRequestsPage';
import MyClockPage from '@/pages/staff/MyClockPage';
import MyExpensesPage from '@/pages/staff/MyExpensesPage';
import ExpenseApprovalPage from '@/pages/staff/ExpenseApprovalPage';
import MyTimesheetsV2Page from '@/pages/timesheets/MyTimesheetsV2Page';
import ServiceCatalog from '@/pages/admin/ServiceCatalog';
import NdisMasterCatalogue from '@/pages/admin/NdisMasterCatalogue';
import BranchCatalogue from '@/pages/admin/BranchCatalogue';
import FundingPlans from '@/pages/admin/FundingPlans';
import AuditLogPage from '@/pages/admin/AuditLogPage';
import QrAuditPage from '@/pages/admin/QrAuditPage';
import NotificationsPage from '@/pages/notifications/NotificationsPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
import IncidentListPage from '@/pages/incidents/IncidentListPage';
import IncidentCreatePage from '@/pages/incidents/IncidentCreatePage';
import IncidentDetailPage from '@/pages/incidents/IncidentDetailPage';
import IncidentIntelligencePage from '@/pages/incidents/IncidentIntelligencePage';
import ClaimListPage from '@/pages/finance/ClaimListPage';
import ClaimDetailPage from '@/pages/finance/ClaimDetailPage';
import ComplianceDashboardPage from '@/pages/compliance/ComplianceDashboardPage';
import RestrictivePracticesPage from '@/pages/compliance/RestrictivePracticesPage';
import LeaveApprovalsPage from '@/pages/hr/LeaveApprovalsPage';
import PortalLeavePage from '@/pages/portal/PortalLeavePage';
import PortalAvailabilityPage from '@/pages/portal/PortalAvailabilityPage';
import MedicationDashboardPage from '@/pages/clinical/MedicationDashboardPage';
import RecruitmentPipelinePage from '@/pages/hr/RecruitmentPipelinePage';
import CandidateProfilePage from '@/pages/hr/CandidateProfilePage';
import HRDashboardPage from '@/pages/hr/HRDashboardPage';
import OnboardingPage from '@/pages/hr/OnboardingPage';
import HousesListPage from '@/pages/accommodation/HousesListPage';
import HouseRoomsPage from '@/pages/accommodation/HouseRoomsPage';
import HouseDashboardPage from '@/pages/houses/HouseDashboardPage';
import MaintenanceListPage from '@/pages/maintenance/MaintenanceListPage';
import PayrollPeriodsPage from '@/pages/finance/payroll/PayrollPeriodsPage';
import PayrollBatchesPage from '@/pages/finance/payroll/PayrollBatchesPage';
import PayrollBatchDetailPage from '@/pages/finance/payroll/PayrollBatchDetailPage';
import PayrollExceptionsPage from '@/pages/finance/payroll/PayrollExceptionsPage';
import PayrollAdminPage from '@/pages/admin/PayrollAdminPage';
import MissedClaimsPage from '@/pages/finance/MissedClaimsPage';
import FinancePipelinePage from '@/pages/finance/FinancePipelinePage';
import WorkforceIntelligencePage from '@/pages/workforce/WorkforceIntelligencePage';
import SILIntelligencePage from '@/pages/accommodation/SILIntelligencePage';
import StaffCostsReportPage from '@/pages/reports/StaffCostsReportPage';

// Day-11: Dashboards
import ExecutiveDashboardPage from '@/pages/dashboards/ExecutiveDashboardPage';
import OperationsDashboardPage from '@/pages/dashboards/OperationsDashboardPage';
import FinanceDashboardPage from '@/pages/dashboards/FinanceDashboardPage';
import HRDashboardPageNew from '@/pages/dashboards/HRDashboardPage';
import ClinicalDashboardPage from '@/pages/dashboards/ClinicalDashboardPage';
import AlertsPage from '@/pages/dashboards/AlertsPage';
import ActionDashboard from '@/pages/dashboards/ActionDashboard';
import SavedReportsPage from '@/pages/reports/SavedReportsPage';
import ReportBuilderPage from '@/pages/reports/ReportBuilderPage';

// Day-12: Portal Pages
import PortalLoginPage from '@/pages/portal/PortalLoginPage';
import PortalForgotPasswordPage from '@/pages/portal/PortalForgotPasswordPage';
import PortalSetPasswordPage from '@/pages/portal/PortalSetPasswordPage';
import PortalDashboardPage from '@/pages/portal/PortalDashboardPage';
import PortalShiftsPage from '@/pages/portal/PortalShiftsPage';
import PortalCarePlanPage from '@/pages/portal/PortalCarePlanPage';
import PortalGoalsPage from '@/pages/portal/PortalGoalsPage';
import PortalMedicationsPage from '@/pages/portal/PortalMedicationsPage';
import PortalInvoicesPage from '@/pages/portal/PortalInvoicesPage';
import PortalMessagesPage from '@/pages/portal/PortalMessagesPage';
import PortalReferralPage from '@/pages/portal/PortalReferralPage';
import ParticipantIntakeWizardPage from '@/pages/intake/ParticipantIntakeWizardPage';

// Day-13: Document Management
import DocumentLibraryPage from '@/pages/documents/DocumentLibraryPage';
import DocumentTemplatesPage from '@/pages/documents/DocumentTemplatesPage';
import ESignRequestsPage from '@/pages/documents/ESignRequestsPage';
import DocumentExportPage from '@/pages/documents/DocumentExportPage';
import StaffPoliciesPage from '@/pages/staff/StaffPoliciesPage';
import ParticipantDocumentRequestsPage from '@/pages/participants/ParticipantDocumentRequestsPage';
import ParticipantReadinessCentrePage from '@/pages/participants/ParticipantReadinessCentrePage';
import BillingReadinessPage from '@/pages/finance/BillingReadinessPage';
import BillingRecordsPage from '@/pages/finance/BillingRecordsPage';
import WorkerScreeningPage from '@/pages/hr/WorkerScreeningPage';
import WorkerFeedbackPage from '@/pages/hr/WorkerFeedbackPage';
import ServiceEvidencePage from '@/pages/timesheets/ServiceEvidencePage';
import MyFeedbackPage from '@/pages/staff/MyFeedbackPage';
import ParticipantChartDetailPage from '@/pages/participants/charts/ParticipantChartDetailPage';
import ReviewOutcomesExplorerPage from '@/pages/participants/charts/ReviewOutcomesExplorerPage';
import PortalDocumentsPage from '@/pages/portal/PortalDocumentsPage';

// Day-14: AI Intelligence Layer
import FundingForecastPage from '@/pages/insights/FundingForecastPage';
import RiskDashboardPage from '@/pages/insights/RiskDashboardPage';
import RecommendationsPage from '@/pages/insights/RecommendationsPage';
import AICopilotPage from '@/pages/ai/AICopilotPage';
import AIIntelligencePage from '@/pages/ai/AIIntelligencePage';
import SavedInsightsPage from '@/pages/insights/SavedInsightsPage';

// Staff Dashboard
import StaffDashboard from '@/pages/staff/StaffDashboard';
import MyTripLogsPage from '@/pages/staff/MyTripLogsPage';
import MyParticipantsPage from '@/pages/staff/MyParticipantsPage';
import HandoverFeedPage from '@/pages/staff/HandoverFeedPage';
import StaffParticipantProfilePage from '@/pages/staff/StaffParticipantProfilePage';
import PhasePlaceholderPage from '@/pages/staff/PhasePlaceholderPage';
import MyTasksPage from '@/pages/staff/MyTasksPage';
import LearningPage from '@/pages/staff/LearningPage';
import ResourcesPage from '@/pages/staff/ResourcesPage';
import MyEmploymentPage from '@/pages/staff/MyEmploymentPage';
import PayrollPage from '@/pages/staff/PayrollPage';
import StaffSettingsPage from '@/pages/staff/SettingsPage';
import { GraduationCap as LearningIcon, Wallet as PayrollIcon, IdCard as EmploymentIcon, Settings as SettingsPageIcon } from 'lucide-react';

// Day-15: Mobile Field Operations
import MobileStaffDashboardPage from '@/pages/mobile/MobileStaffDashboardPage';
import MobileDeviceBlocked from '@/pages/mobile/MobileDeviceBlocked';
import MobileProgressNotePage from '@/pages/mobile/MobileProgressNotePage';
import MobileIncidentPage from '@/pages/mobile/MobileIncidentPage';
import MobileHandoverPage from '@/pages/mobile/MobileHandoverPage';
import MobilePendingSyncPage from '@/pages/mobile/MobilePendingSyncPage';
import MobileMedicationPage from '@/pages/mobile/MobileMedicationPage';
import MobileChartsPage from '@/pages/mobile/MobileChartsPage';
import MobileChartEntryPage from '@/pages/mobile/MobileChartEntryPage';
import MobileSafetyPage from '@/pages/mobile/MobileSafetyPage';
import MobileAttachmentUploadPage from '@/pages/mobile/MobileAttachmentUploadPage';
import MobileNotificationsPage from '@/pages/mobile/MobileNotificationsPage';
import MobileTransportLogPage from '@/pages/mobile/MobileTransportLogPage';
import MobileShiftDetailPage from '@/pages/mobile/MobileShiftDetailPage';
import MobileCheckinExceptionsPage from '@/pages/mobile/MobileCheckinExceptionsPage';
import MobileParticipantProfilePage from '@/pages/mobile/MobileParticipantProfilePage';
import MobileCarePlanBriefPage from '@/pages/mobile/MobileCarePlanBriefPage';
import TransportVehiclesAdminPage from '@/pages/admin/transport/TransportVehiclesAdminPage';
import TransportLogsAdminPage from '@/pages/admin/transport/TransportLogsAdminPage';
import TransportSettingsAdminPage from '@/pages/admin/transport/TransportSettingsAdminPage';

// Day-16: Communications & Integrations UI
import CommunicationTemplatesPage from '@/pages/settings/CommunicationTemplatesPage';
import { SMSLogPage, EmailLogPage } from '@/pages/communications/CommunicationLogPages';
import CommunicationHubPage from '@/pages/communications/CommunicationHubPage';
import AccountingIntegrationPage from '@/pages/settings/AccountingIntegrationPage';
import PublicFormManagementPage from '@/pages/settings/PublicFormManagementPage';
import ClaimExportPage from '@/pages/finance/ClaimExportPage';
import WebhookManagementPage from '@/pages/settings/WebhookManagementPage';

// Section 9: Document + Onboarding Intelligence
import DocumentIntelligencePage from '@/pages/compliance/DocumentIntelligencePage';

// Day 12: Exception Queue
import ExceptionQueuePage from '@/pages/admin/ExceptionQueuePage';

// Day 17: Corrections
import CorrectionsPage from '@/pages/admin/CorrectionsPage';

// Day 18: Invoice Regeneration
import InvoiceRegenerationPage from '@/pages/finance/InvoiceRegenerationPage';

// Admin Feature Flags UI
import FeatureFlagsPage from '@/pages/admin/FeatureFlagsPage';
import ParityAuditPage from '@/pages/admin/ParityAuditPage';
import LiveSplitBillingGovernancePage from '@/pages/admin/LiveSplitBillingGovernancePage';
import ActivationCommandCenterPage from '@/pages/admin/ActivationCommandCenterPage';

// Ratio Engine
import RatioRulesPage from '@/pages/admin/RatioRulesPage';
import SleepoverSettingsPage from '@/pages/admin/SleepoverSettingsPage';
import SleepoverEstimateComparisonPage from '@/pages/admin/SleepoverEstimateComparisonPage';

// Release, Reports, Digest
import ReleaseSignoffPage from '@/pages/admin/ReleaseSignoffPage';
import ReportsPage from '@/pages/admin/ReportsPage';
import DigestPage from '@/pages/admin/DigestPage';
import RolloutDashboardPage from '@/pages/admin/RolloutDashboardPage';
// Day 15: Smart Alerts
import SmartAlertsPage from '@/pages/admin/SmartAlertsPage';
import SmartAlertDetailPage from '@/pages/admin/SmartAlertDetailPage';
// Day 17: Predictive Staffing
import PredictiveStaffingPage from '@/pages/admin/PredictiveStaffingPage';
// Day 18: Revenue Optimization
import RevenueOptimizationPage from '@/pages/admin/RevenueOptimizationPage';
// Day 19: Compliance & Audit
import ComplianceAuditPage from '@/pages/admin/ComplianceDashboardPage';
// Day 20: Audit Reporting
import AuditReportingPage from '@/pages/admin/AuditReportingPage';
// Day 24: Soft Launch
import SoftLaunchPage from '@/pages/admin/SoftLaunchPage';
// Day 25: Client Onboarding
import OnboardingDashboardPage from '@/pages/admin/OnboardingDashboardPage';
import OnboardingWizardPage from '@/pages/admin/OnboardingWizardPage';
import OnboardingDetailPage from '@/pages/admin/OnboardingDetailPage';
import OnboardingImportPage from '@/pages/admin/OnboardingImportPage';
import OnboardingReviewPage from '@/pages/admin/OnboardingReviewPage';
// Day 2: Group Sessions
import GroupSessionsPage from '@/pages/admin/GroupSessionsPage';
import GroupSessionDetailPage from '@/pages/admin/GroupSessionDetailPage';

// Day-17: Roster Intelligence
import RosterInsightsPage from '@/pages/roster/RosterInsightsPage';
import BulkAssignShiftsPage from '@/pages/roster/BulkAssignShiftsPage';

// Day-17b: Advanced Rostering
import RecurringSeriesPage from '@/pages/roster/RecurringSeriesPage';
import ShiftTemplatesPage from '@/pages/roster/ShiftTemplatesPage';
import WorkforceDashboardPage from '@/pages/roster/WorkforceDashboardPage';
import ShiftSwapRequestsPage from '@/pages/roster/ShiftSwapRequestsPage';
import SILRosterPage from '@/pages/roster/SILRosterPage';
import CentreSessionsPage from '@/pages/admin/CentreSessionsPage';
import MobileTaskTemplatesPage from '@/pages/admin/MobileTaskTemplatesPage';
import NoteTemplatesPage from '@/pages/admin/NoteTemplatesPage';
import IncidentReviewPage from '@/pages/admin/IncidentReviewPage';
import TeamRosterPage from '@/pages/roster/TeamRosterPage';
import RosterCalendarPage from '@/pages/roster/RosterCalendarPage';
import CalendarV3 from '@/pages/calendar/CalendarV3';
import AssetManagementPage from '@/pages/assets/AssetManagementPage';
import StaffAvailabilityPage from '@/pages/roster/StaffAvailabilityPage';
import MyShiftsPage from '@/pages/roster/MyShiftsPage';

// Teams
import TeamsListPage from '@/pages/teams/TeamsListPage';
import TeamFormPage from '@/pages/teams/TeamFormPage';
import TeamDetailPage from '@/pages/teams/TeamDetailPage';

// Accommodation Extended
import AccommodationDashboardPage from '@/pages/accommodation/AccommodationDashboardPage';

// Day-18: Engagement
import AppointmentsPage from '@/pages/engagement/AppointmentsPage';
import CommunicationCentrePage from '@/pages/engagement/CommunicationCentrePage';
import FeedbackPage, { PortalFeedbackPage } from '@/pages/engagement/FeedbackPage';
import AnnouncementsPage from '@/pages/engagement/AnnouncementsPage';

// Day-19: Security
import SecurityDashboardPage from '@/pages/admin/SecurityDashboardPage';
import SecurityAlertsPage from '@/pages/admin/SecurityAlertsPage';
// Day-20: System Admin
import SystemAdminPage from '@/pages/admin/SystemAdminPage';
// Branding
import BrandingSettingsPage from '@/pages/admin/BrandingSettingsPage';
// Onboarding Portal
import OnboardingPortalPage from '@/pages/onboarding/OnboardingPortalPage';
import SetupPasswordPage from '@/pages/auth/SetupPasswordPage';
import DocumentReviewDashboardPage from '@/pages/hr/DocumentReviewDashboardPage';

// Form Builder
import FormsDashboardPage from '@/pages/forms/FormsDashboardPage';
import FormTemplatesPage from '@/pages/forms/FormTemplatesPage';
import FormTemplateBuilderPage from '@/pages/forms/FormTemplateBuilderPage';
import FormTemplateDetailPage from '@/pages/forms/FormTemplateDetailPage';
import FormAssignmentsPage from '@/pages/forms/FormAssignmentsPage';
import FormFillPage from '@/pages/forms/FormFillPage';
import FormPrintPage from '@/pages/forms/FormPrintPage';
import FormLibraryPage from '@/pages/forms/FormLibraryPage';
import FormLibraryItemPage from '@/pages/forms/FormLibraryItemPage';
import FormLibraryPacksPage from '@/pages/forms/FormLibraryPacksPage';
import { canBrowseLibrary, canAdminLibrary } from '@/lib/libraryAccess';
import { canViewRegisters } from '@/lib/auditRegisters';
import { canViewReadiness } from '@/lib/readiness';
import ReadinessOverviewPage from '@/pages/readiness/ReadinessOverviewPage';
import ReadinessOutcomesPage from '@/pages/readiness/ReadinessOutcomesPage';
import ReadinessOutcomePage from '@/pages/readiness/ReadinessOutcomePage';
import ReadinessEvidenceIndexPage from '@/pages/readiness/ReadinessEvidenceIndexPage';
import EvidenceBundlePage from '@/pages/readiness/EvidenceBundlePage';
import { canExportReadiness } from '@/lib/readiness';
import AuditRegistersHubPage from '@/pages/audit/AuditRegistersHubPage';
import AuditRegisterPage from '@/pages/audit/AuditRegisterPage';
import AuditRegisterPrintPage from '@/pages/audit/AuditRegisterPrintPage';
import DashboardLanding from '@/pages/dashboards/DashboardLanding';
import IntakeHubPage from '@/pages/intake/IntakeHubPage';
import { isPlatformAdmin } from '@/lib/platformAdmin';

function ProtectedRoute({ children, requireAdmin = false, requireSuperAdmin = false, requirePlatformAdmin = false, allowRoles = null, allowStaff = false, blockStaff = false, requirePermission = null, requireAccess = null }) {
  const { user, loading, isAdmin, hasPermission, mustChangePassword, onPasswordChanged } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen bg-background"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  
  // Non-admin shells are worker-type roles (Day 15: derived from useAuth, not the role name — a custom role must
  // never bounce between /dashboard and itself, which rendered a blank screen).
  const isStaffRole = !isAdmin;
  
  // Redirect staff away from admin areas
  if (requireAdmin && !isAdmin) return <Navigate to="/staff/dashboard" replace />;
  if (requireSuperAdmin && user?.role_name !== 'Super Admin') return <Navigate to={isStaffRole ? "/staff/dashboard" : "/dashboard"} replace />;
  // Platform-team pages (mirrors the sidebar platformOnly / allowRoles rules).
  if (requirePlatformAdmin && !isPlatformAdmin(user)) return <Navigate to={isStaffRole ? "/staff/dashboard" : "/dashboard"} replace />;
  if (allowRoles && !isPlatformAdmin(user) && !allowRoles.includes(user?.role_name)) return <Navigate to={isStaffRole ? "/staff/dashboard" : "/dashboard"} replace />;
  // Bounce Support Workers away from org-wide registers they must not browse
  // (they have dedicated /staff/my-participants). Elevated roles are unaffected.
  if (blockStaff && isStaffRole) return <Navigate to="/staff/dashboard" replace />;
  // Explicit permission gate — mirrors backend require_permission so the UI
  // never surfaces a page the API will 403 (e.g. timesheets.approve).
  if (requirePermission && !hasPermission(requirePermission)) return <Navigate to={isStaffRole ? "/staff/dashboard" : "/dashboard"} replace />;
  // Composite gate (e.g. Template Library: forms.view AND one library capability) — mirrors the backend browse rule.
  if (requireAccess && !requireAccess(hasPermission)) return <Navigate to={isStaffRole ? "/staff/dashboard" : "/dashboard"} replace />;
  
  return (
    <AppShell>
      {mustChangePassword && (
        <ForceChangePasswordModal open={true} onSuccess={onPasswordChanged} />
      )}
      {children}
    </AppShell>
  );
}

function AuthRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen bg-background"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (user) {
    // Redirect worker-type roles to their dashboard, everyone else to the admin shell landing (Day 15)
    return <Navigate to={isAdmin ? "/dashboard" : "/staff/dashboard"} replace />;
  }
  return children;
}

function PortalAuthRoute({ children }) {
  const { user, loading } = usePortalAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen bg-background"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (user) return <Navigate to="/portal/dashboard" replace />;
  return children;
}

function PortalProtectedRoute({ children }) {
  const { user, loading } = usePortalAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen bg-background"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/portal/login" replace />;
  return <PortalShell>{children}</PortalShell>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      <Route path="/account-deletion" element={<DeleteAccountPage />} />
      <Route path="/onboarding/:token" element={<OnboardingPortalPage />} />
      <Route path="/setup-password" element={<SetupPasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Day-12: External Portal Routes (separate auth) */}
      <Route path="/portal/login" element={<PortalAuthRoute><PortalLoginPage /></PortalAuthRoute>} />
      <Route path="/portal/forgot-password" element={<PortalForgotPasswordPage />} />
      <Route path="/portal/accept-invite" element={<PortalSetPasswordPage mode="invite" />} />
      <Route path="/portal/reset-password" element={<PortalSetPasswordPage mode="reset" />} />
      <Route path="/portal/dashboard" element={<PortalProtectedRoute><PortalDashboardPage /></PortalProtectedRoute>} />
      <Route path="/portal/shifts" element={<PortalProtectedRoute><PortalShiftsPage /></PortalProtectedRoute>} />
      <Route path="/portal/care-plan" element={<PortalProtectedRoute><PortalCarePlanPage /></PortalProtectedRoute>} />
      <Route path="/portal/goals" element={<PortalProtectedRoute><PortalGoalsPage /></PortalProtectedRoute>} />
      <Route path="/portal/medications" element={<PortalProtectedRoute><PortalMedicationsPage /></PortalProtectedRoute>} />
      <Route path="/portal/invoices" element={<PortalProtectedRoute><PortalInvoicesPage /></PortalProtectedRoute>} />
      <Route path="/portal/messages" element={<PortalProtectedRoute><PortalMessagesPage /></PortalProtectedRoute>} />
      <Route path="/portal/referral" element={<PortalProtectedRoute><PortalReferralPage /></PortalProtectedRoute>} />
      <Route path="/portal/documents" element={<PortalProtectedRoute><PortalDocumentsPage /></PortalProtectedRoute>} />
      <Route path="/portal/feedback" element={<PortalProtectedRoute><PortalFeedbackPage /></PortalProtectedRoute>} />

      {/* Admin */}
      <Route path="/admin/users" element={<ProtectedRoute requireAdmin><StaffManagement /></ProtectedRoute>} />
      <Route path="/admin/roles" element={<ProtectedRoute requireAdmin><RolesPage /></ProtectedRoute>} />
      <Route path="/admin/care-plan-templates" element={<ProtectedRoute requireAdmin><CarePlanTemplates /></ProtectedRoute>} />
      <Route path="/admin/audit" element={<ProtectedRoute requireAdmin><AuditLogPage /></ProtectedRoute>} />
      <Route path="/admin/qr-audit" element={<ProtectedRoute requireAdmin><QrAuditPage /></ProtectedRoute>} />

      {/* 7.4 Settings */}
      <Route path="/org/settings" element={<ProtectedRoute requireAdmin><OrgSettingsPage /></ProtectedRoute>} />

      {/* 7.5 Staff */}
      <Route path="/staff/list" element={<ProtectedRoute requireAdmin><StaffListPage /></ProtectedRoute>} />
      <Route path="/staff/:staffId" element={<ProtectedRoute requireAdmin><StaffDetailPage /></ProtectedRoute>} />
      <Route path="/workforce/availability" element={<ProtectedRoute requireAdmin><StaffAvailabilityViewerPage /></ProtectedRoute>} />

      {/* 7.6 Participants */}
      <Route path="/participants/list" element={<ProtectedRoute blockStaff><ParticipantListPage /></ProtectedRoute>} />
      <Route path="/participants/:participantId" element={<ProtectedRoute><ParticipantDetailPage /></ProtectedRoute>} />

      {/* 7.7 Rostering */}
      <Route path="/roster/calendar" element={<ProtectedRoute requirePlatformAdmin><RosterAdmin /></ProtectedRoute>} />

      {/* Day-17b: Advanced Rostering */}
      <Route path="/roster/recurring" element={<ProtectedRoute requireAdmin requirePermission="roster.manage_templates"><RecurringSeriesPage /></ProtectedRoute>} />   {/* RP-3 R4: legacy, hidden from Coordinator */}
      <Route path="/roster/templates" element={<ProtectedRoute requireAdmin><ShiftTemplatesPage /></ProtectedRoute>} />
      <Route path="/roster/workforce" element={<ProtectedRoute requireAdmin><WorkforceDashboardPage /></ProtectedRoute>} />
      <Route path="/roster/swap-requests" element={<ProtectedRoute requireAdmin><ShiftSwapRequestsPage /></ProtectedRoute>} />
      <Route path="/roster/sil" element={<ProtectedRoute requireAdmin><SILRosterPage /></ProtectedRoute>} />
      <Route path="/admin/centre-sessions" element={<ProtectedRoute requireAdmin><CentreSessionsPage /></ProtectedRoute>} />
      <Route path="/admin/task-templates" element={<ProtectedRoute requireAdmin><MobileTaskTemplatesPage /></ProtectedRoute>} />
      <Route path="/admin/note-templates" element={<ProtectedRoute requireAdmin><NoteTemplatesPage /></ProtectedRoute>} />
      <Route path="/admin/incident-review" element={<ProtectedRoute requireAdmin><IncidentReviewPage /></ProtectedRoute>} />
      <Route path="/roster/teams" element={<ProtectedRoute requireAdmin><TeamRosterPage /></ProtectedRoute>} />
      <Route path="/roster/visual" element={<ProtectedRoute requirePlatformAdmin><RosterCalendarPage /></ProtectedRoute>} />
      <Route path="/roster/scheduling-board" element={<ProtectedRoute requireAdmin><CalendarV3 /></ProtectedRoute>} />
      <Route path="/asset-management" element={<ProtectedRoute requireAdmin><AssetManagementPage /></ProtectedRoute>} />
      <Route path="/roster/staff-availability" element={<ProtectedRoute requireAdmin><StaffAvailabilityPage /></ProtectedRoute>} />
      <Route path="/roster/my-shifts" element={<ProtectedRoute><MyShiftsPage /></ProtectedRoute>} />

      {/* Services (admin) */}
      <Route path="/admin/services" element={<ProtectedRoute requireAdmin><ServiceCatalog /></ProtectedRoute>} />
      <Route path="/admin/ndis-catalogue" element={<ProtectedRoute requireSuperAdmin><NdisMasterCatalogue /></ProtectedRoute>} />
      <Route path="/admin/branch-catalogue" element={<ProtectedRoute requirePermission="services.manage"><BranchCatalogue /></ProtectedRoute>} />
      <Route path="/admin/funding-plans" element={<ProtectedRoute requireAdmin><FundingPlans /></ProtectedRoute>} />
      <Route path="/admin/assets" element={<ProtectedRoute requireAdmin><AssetManagementPage /></ProtectedRoute>} />

      {/* 7.8 Staff Portal */}
      <Route path="/portal/roster" element={<ProtectedRoute><PortalRosterPage /></ProtectedRoute>} />
      <Route path="/portal/shift/:shiftId" element={<ProtectedRoute><StaffPortalRoute title="Shift Detail" subtitle="Shift details and attendance"><ShiftDetail /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/portal/leave" element={<ProtectedRoute><StaffPortalRoute title="Leave" subtitle="Request time off and view balance"><PortalLeavePage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/portal/availability" element={<ProtectedRoute><StaffPortalRoute title="Availability" subtitle="When you can work"><PortalAvailabilityPage /></StaffPortalRoute></ProtectedRoute>} />

      {/* 7.9 Timesheets */}
      <Route path="/timesheets/pending" element={<ProtectedRoute requireAdmin><TimesheetApproval /></ProtectedRoute>} />
      <Route path="/timesheets/approval" element={<ProtectedRoute requirePermission="timesheets.view"><TimesheetApprovalV2Page /></ProtectedRoute>} />
      <Route path="/timesheets/my-v2" element={<ProtectedRoute><StaffPortalRoute title="My Timesheets" subtitle="View and submit your timesheets"><MyTimesheetsV2Page /></StaffPortalRoute></ProtectedRoute>} />

      {/* Day 12: Exception Queue */}
      <Route path="/admin/attendance-exceptions" element={<ProtectedRoute requireAdmin><ExceptionQueuePage /></ProtectedRoute>} />

      {/* Day 17: Corrections */}
      <Route path="/admin/corrections" element={<ProtectedRoute requireAdmin><CorrectionsPage /></ProtectedRoute>} />

      {/* 7.10 Finance */}
      <Route path="/finance/invoices" element={<ProtectedRoute requireAdmin><Invoices /></ProtectedRoute>} />
      <Route path="/finance/billing-readiness" element={<ProtectedRoute requireAdmin requirePermission="timesheets.view"><BillingReadinessPage /></ProtectedRoute>} />
      <Route path="/finance/billing-records" element={<ProtectedRoute requireAdmin requirePermission="invoices.manage"><BillingRecordsPage /></ProtectedRoute>} />
      <Route path="/compliance/worker-screening" element={<ProtectedRoute requireAdmin requirePermission="staff.hr.view"><WorkerScreeningPage /></ProtectedRoute>} />
      <Route path="/compliance/worker-feedback" element={<ProtectedRoute requireAdmin requirePermission="matching.view"><WorkerFeedbackPage /></ProtectedRoute>} />
      <Route path="/timesheets/evidence" element={<ProtectedRoute requireAdmin requirePermission="timesheets.view"><ServiceEvidencePage /></ProtectedRoute>} />
      <Route path="/staff/feedback" element={<ProtectedRoute requirePermission="matching.feedback.respond"><StaffPortalRoute title="Feedback about my shifts" subtitle="Respond when a manager asks for your account"><MyFeedbackPage /></StaffPortalRoute></ProtectedRoute>} />

      {/* Day 18: Invoice Regeneration */}
      <Route path="/finance/invoice-regeneration" element={<ProtectedRoute requireAdmin><InvoiceRegenerationPage /></ProtectedRoute>} />

      {/* Notifications */}
      <Route path="/notifications" element={<ProtectedRoute><StaffPortalRoute title="Notifications" subtitle="Recent updates"><NotificationsPage /></StaffPortalRoute></ProtectedRoute>} />

      {/* Compliance / Incidents */}
      <Route path="/compliance/incidents" element={<ProtectedRoute><StaffPortalRoute title="Incidents" subtitle="Safety and compliance"><IncidentListPage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/compliance/incidents/new" element={<ProtectedRoute><StaffPortalRoute title="Report Incident"><IncidentCreatePage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/compliance/incidents/intelligence" element={<ProtectedRoute><IncidentIntelligencePage /></ProtectedRoute>} />
      <Route path="/compliance/incidents/:incidentId" element={<ProtectedRoute><StaffPortalRoute title="Incident Details"><IncidentDetailPage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/compliance/staff" element={<ProtectedRoute requireAdmin><ComplianceDashboardPage /></ProtectedRoute>} />
      <Route path="/compliance/restrictive-practices" element={<ProtectedRoute><RestrictivePracticesPage /></ProtectedRoute>} />
      <Route path="/compliance/document-intelligence" element={<ProtectedRoute><DocumentIntelligencePage /></ProtectedRoute>} />
      <Route path="/hr/leave" element={<ProtectedRoute requireAdmin><LeaveApprovalsPage /></ProtectedRoute>} />
      <Route path="/hr/dashboard" element={<ProtectedRoute requireAdmin><HRDashboardPage /></ProtectedRoute>} />
      <Route path="/hr/recruitment" element={<ProtectedRoute requireAdmin><RecruitmentPipelinePage /></ProtectedRoute>} />
      <Route path="/hr/candidates/:candidateId" element={<ProtectedRoute requireAdmin><CandidateProfilePage /></ProtectedRoute>} />
      <Route path="/hr/onboarding/:staffId" element={<ProtectedRoute requireAdmin><OnboardingPage /></ProtectedRoute>} />
      <Route path="/clinical/medications" element={<ProtectedRoute requireAdmin><MedicationDashboardPage /></ProtectedRoute>} />

      {/* Finance: Claims */}
      <Route path="/finance/claims" element={<ProtectedRoute requireAdmin><ClaimListPage /></ProtectedRoute>} />
      <Route path="/finance/claims/:claimId" element={<ProtectedRoute requireAdmin><ClaimDetailPage /></ProtectedRoute>} />
      <Route path="/finance/missed-claims" element={<ProtectedRoute requireAdmin><MissedClaimsPage /></ProtectedRoute>} />
      <Route path="/finance/pipeline" element={<ProtectedRoute><FinancePipelinePage /></ProtectedRoute>} />
      <Route path="/workforce/intelligence" element={<ProtectedRoute><WorkforceIntelligencePage /></ProtectedRoute>} />
      <Route path="/accommodation/sil-intelligence" element={<ProtectedRoute requireAdmin><SILIntelligencePage /></ProtectedRoute>} />

      {/* Day-9: Accommodation/SIL */}
      <Route path="/accommodation/houses" element={<ProtectedRoute><StaffPortalRoute title="Properties / Houses" subtitle="View SIL houses and properties"><HousesListPage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/accommodation/houses/:houseId/rooms" element={<ProtectedRoute><StaffPortalRoute title="Rooms" subtitle="Rooms and occupancy"><HouseRoomsPage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/houses/:houseId" element={<ProtectedRoute><HouseDashboardPage /></ProtectedRoute>} />
      <Route path="/maintenance" element={<ProtectedRoute><MaintenanceListPage /></ProtectedRoute>} />

      {/* Day-10: Payroll */}
      <Route path="/finance/payroll-periods" element={<ProtectedRoute requireAdmin><PayrollPeriodsPage /></ProtectedRoute>} />
      <Route path="/finance/payroll" element={<ProtectedRoute requireAdmin><PayrollBatchesPage /></ProtectedRoute>} />
      <Route path="/finance/payroll/:batchId" element={<ProtectedRoute requireAdmin><PayrollBatchDetailPage /></ProtectedRoute>} />
      <Route path="/finance/payroll/:batchId/exceptions" element={<ProtectedRoute requireAdmin><PayrollExceptionsPage /></ProtectedRoute>} />
      <Route path="/reports/staff-costs" element={<ProtectedRoute requireAdmin><StaffCostsReportPage /></ProtectedRoute>} />
      <Route path="/admin/payroll" element={<ProtectedRoute requireAdmin><PayrollAdminPage /></ProtectedRoute>} />

      {/* Day-11: Management Dashboards (New Routes) - ADMIN ONLY */}
      <Route path="/dashboard/operations" element={<ProtectedRoute requireAdmin><OperationsDashboardPage /></ProtectedRoute>} />
      <Route path="/dashboard/finance" element={<ProtectedRoute requireAdmin><FinanceDashboardPage /></ProtectedRoute>} />
      <Route path="/dashboard/hr" element={<ProtectedRoute requireAdmin><HRDashboardPageNew /></ProtectedRoute>} />
      <Route path="/dashboard/clinical" element={<ProtectedRoute requireAdmin><ClinicalDashboardPage /></ProtectedRoute>} />
      <Route path="/dashboard/actions" element={<ProtectedRoute requireAdmin><ActionDashboard /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute requireAdmin><DashboardLanding /></ProtectedRoute>} />
      
      {/* Staff-specific dashboard */}
      <Route path="/staff/dashboard" element={<ProtectedRoute><StaffDashboardRoute /></ProtectedRoute>} />
      {/* Support Workers reach a Participant Profile ONLY via an authorised shift —
          the standalone participant directory is not available in the staff portal. */}
      <Route path="/staff/my-participants" element={<Navigate to="/staff/participants" replace />} />
      {/* Phase 2 — approved nav targets + compatibility redirects */}
      <Route path="/staff" element={<Navigate to="/staff/dashboard" replace />} />
      <Route path="/staff/participants" element={<ProtectedRoute><StaffPortalRoute title="My Participants" subtitle="Participants assigned to your shifts"><MyParticipantsPage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/staff/participants/:participantId" element={<ProtectedRoute><StaffPortalRoute title="Participant" subtitle="Care, clinical, notes and safety"><StaffParticipantProfilePage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/staff/handovers" element={<ProtectedRoute><StaffPortalRoute title="Handovers" subtitle="Shift handovers awaiting your acknowledgement"><HandoverFeedPage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/staff/learning" element={<ProtectedRoute><StaffPortalRoute title="Learning" subtitle="Training, competencies, certificates and policies"><LearningPage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/staff/tasks" element={<ProtectedRoute><StaffPortalRoute title="My Tasks" subtitle="Participant tasks assigned to you"><MyTasksPage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/staff/resources" element={<ProtectedRoute><StaffPortalRoute title="Resources" subtitle="Help centre, FAQs, forms and support"><ResourcesPage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/staff/payroll" element={<ProtectedRoute><StaffPortalRoute title="Payroll" subtitle="Reimbursements, payslips, leave balances and payment history"><PayrollPage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/staff/employment" element={<ProtectedRoute><StaffPortalRoute title="My Employment" subtitle="Personal details, qualifications and documents"><MyEmploymentPage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><StaffPortalRoute title="Settings" subtitle="Password and preferences"><StaffSettingsPage /></StaffPortalRoute></ProtectedRoute>} />
      {/* Staff roster + shift detail + open shifts — wired so per-staff
          Check-In/Out works for SIL 2:1 multi-staff shifts. */}
      <Route path="/staff/roster" element={<ProtectedRoute><StaffPortalRoute title="My Roster" subtitle="Your upcoming shifts"><MyRoster /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/staff/transport/new" element={<ProtectedRoute><StaffPortalRoute title="Log a trip" subtitle="Record a trip in your own or a company vehicle"><MobileTransportLogPage portal /></StaffPortalRoute></ProtectedRoute>} />   {/* BS3-026 */}
      <Route path="/staff/attachments" element={<ProtectedRoute><StaffPortalRoute title="Upload Attachments" subtitle="Capture photos or upload documents"><MobileAttachmentUploadPage portal /></StaffPortalRoute></ProtectedRoute>} />   {/* BS3-025 */}
      <Route path="/staff/shift/:shiftId" element={<ProtectedRoute><StaffPortalRoute title="Shift Detail" subtitle="Shift details and attendance"><ShiftDetail /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/staff/open-shifts" element={<ProtectedRoute><StaffPortalRoute title="Open Shifts" subtitle="Shifts available for pickup"><OpenShifts /></StaffPortalRoute></ProtectedRoute>} />

      {/* My Work — consolidated Support Worker landing + Phase 1 additions */}
      <Route path="/my/work" element={<ProtectedRoute><MyWorkRoute /></ProtectedRoute>} />
      <Route path="/my/swap-requests" element={<ProtectedRoute><StaffPortalRoute title="Shift Swaps" subtitle="Your swap requests"><MySwapRequestsPage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/my/clock" element={<ProtectedRoute><StaffPortalRoute title="Clock In / Out" subtitle="Start or end your shift"><MyClockPage /></StaffPortalRoute></ProtectedRoute>} />

      {/* Phase 2 — Expenses */}
      <Route path="/my/expenses" element={<ProtectedRoute><StaffPortalRoute title="My Expenses" subtitle="Submit receipts for reimbursement"><MyExpensesPage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/finance/expenses/approvals" element={<ProtectedRoute><ExpenseApprovalPage /></ProtectedRoute>} />
      
      <Route path="/alerts" element={<ProtectedRoute requireAdmin><AlertsPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute requireAdmin><ReportBuilderPage /></ProtectedRoute>} />
      
      {/* Legacy dashboard routes - redirect to new paths */}
      {/* Day-30 cleanup: legacy /dashboards/* redirects removed.
          Canonical paths in active use: /dashboard, /dashboard/operations,
          /dashboard/clinical, /dashboard/finance, /dashboard/hr, /alerts. */}

      {/* Day-12: Intake/Referrals (Internal) */}
      <Route path="/intake/referrals" element={<ProtectedRoute><IntakeHubPage tab="referrals" /></ProtectedRoute>} />
      <Route path="/intake/participants" element={<ProtectedRoute><IntakeHubPage tab="intake" /></ProtectedRoute>} />
      <Route path="/intake/participants/:id" element={<ProtectedRoute><ParticipantIntakeWizardPage /></ProtectedRoute>} />

      {/* Day-13: Document Management */}
      <Route path="/documents" element={<ProtectedRoute><DocumentLibraryPage /></ProtectedRoute>} />
      <Route path="/documents/templates" element={<ProtectedRoute><DocumentTemplatesPage /></ProtectedRoute>} />
      <Route path="/documents/esign" element={<ProtectedRoute><ESignRequestsPage /></ProtectedRoute>} />
      <Route path="/documents/export" element={<ProtectedRoute><DocumentExportPage /></ProtectedRoute>} />
      <Route path="/staff/policies" element={<ProtectedRoute><StaffPortalRoute title="Policies & Resources" subtitle="Reference documents"><StaffPoliciesPage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/participants/:participantId/document-requests" element={<ProtectedRoute><ParticipantDocumentRequestsPage /></ProtectedRoute>} />
      <Route path="/participants/:participantId/readiness-centre" element={<ProtectedRoute><ParticipantReadinessCentrePage /></ProtectedRoute>} />
      <Route path="/participants/:participantId/charts/:chartId" element={<ProtectedRoute><ParticipantChartDetailPage /></ProtectedRoute>} />
      <Route path="/charts/review-outcomes" element={<ProtectedRoute><ReviewOutcomesExplorerPage /></ProtectedRoute>} />

      {/* Day-14: AI Intelligence Layer */}
      <Route path="/insights/funding" element={<ProtectedRoute><FundingForecastPage /></ProtectedRoute>} />
      <Route path="/insights/risk" element={<ProtectedRoute><RiskDashboardPage /></ProtectedRoute>} />
      <Route path="/insights/recommendations" element={<ProtectedRoute><RecommendationsPage /></ProtectedRoute>} />
      <Route path="/insights/saved" element={<ProtectedRoute><SavedInsightsPage /></ProtectedRoute>} />
      <Route path="/ai" element={<ProtectedRoute><AICopilotPage /></ProtectedRoute>} />
      <Route path="/ai/intelligence" element={<ProtectedRoute><AIIntelligencePage /></ProtectedRoute>} />

      {/* Day-15: Mobile Field Operations */}
      <Route path="/mobile/blocked" element={<MobileDeviceBlocked />} />
      <Route path="/mobile" element={<ProtectedRoute><MobileStaffDashboardPage /></ProtectedRoute>} />
      <Route path="/mobile/notes/new" element={<ProtectedRoute><MobileProgressNotePage /></ProtectedRoute>} />
      <Route path="/mobile/incidents/new" element={<ProtectedRoute><MobileIncidentPage /></ProtectedRoute>} />
      <Route path="/mobile/handover/new" element={<ProtectedRoute><MobileHandoverPage /></ProtectedRoute>} />
      <Route path="/mobile/medications/log" element={<ProtectedRoute><MobileMedicationPage /></ProtectedRoute>} />
      <Route path="/mobile/charts" element={<ProtectedRoute><MobileChartsPage /></ProtectedRoute>} />
      <Route path="/mobile/charts/entry" element={<ProtectedRoute><MobileChartEntryPage /></ProtectedRoute>} />
      <Route path="/mobile/safety" element={<ProtectedRoute><MobileSafetyPage /></ProtectedRoute>} />
      <Route path="/mobile/safety/events" element={<ProtectedRoute><MobileSafetyPage /></ProtectedRoute>} />
      <Route path="/mobile/attachments" element={<ProtectedRoute><MobileAttachmentUploadPage /></ProtectedRoute>} />
      <Route path="/mobile/notifications" element={<ProtectedRoute><MobileNotificationsPage /></ProtectedRoute>} />
      <Route path="/mobile/sync" element={<ProtectedRoute><MobilePendingSyncPage /></ProtectedRoute>} />
      <Route path="/mobile/transport/new" element={<ProtectedRoute><MobileTransportLogPage /></ProtectedRoute>} />
      <Route path="/mobile/shifts/:shiftId" element={<ProtectedRoute><MobileShiftDetailPage /></ProtectedRoute>} />
      <Route path="/mobile/exceptions" element={<ProtectedRoute><MobileCheckinExceptionsPage /></ProtectedRoute>} />
      <Route path="/mobile/participants/:participantId" element={<ProtectedRoute><MobileParticipantProfilePage /></ProtectedRoute>} />
      <Route path="/mobile/participants/:participantId/care-plan" element={<ProtectedRoute><MobileCarePlanBriefPage /></ProtectedRoute>} />
      <Route path="/my/trip-logs" element={<ProtectedRoute><StaffPortalRoute title="Trip Logs" subtitle="Log your work trips"><MyTripLogsPage /></StaffPortalRoute></ProtectedRoute>} />
      <Route path="/admin/transport/vehicles" element={<ProtectedRoute requireAdmin><TransportVehiclesAdminPage /></ProtectedRoute>} />
      <Route path="/admin/transport/logs" element={<ProtectedRoute requireAdmin><TransportLogsAdminPage /></ProtectedRoute>} />
      <Route path="/admin/transport/settings" element={<ProtectedRoute requireAdmin><TransportSettingsAdminPage /></ProtectedRoute>} />

      {/* Day-16: Communications & Integrations */}
      <Route path="/settings/communications/templates" element={<ProtectedRoute><CommunicationTemplatesPage /></ProtectedRoute>} />
      <Route path="/communications/hub" element={<ProtectedRoute><CommunicationHubPage /></ProtectedRoute>} />
      <Route path="/communications/sms" element={<ProtectedRoute><SMSLogPage /></ProtectedRoute>} />
      <Route path="/communications/email" element={<ProtectedRoute><EmailLogPage /></ProtectedRoute>} />
      <Route path="/settings/integrations/accounting" element={<ProtectedRoute><AccountingIntegrationPage /></ProtectedRoute>} />
      <Route path="/settings/public-forms" element={<ProtectedRoute><PublicFormManagementPage /></ProtectedRoute>} />
      <Route path="/finance/claims/:id/export" element={<ProtectedRoute><ClaimExportPage /></ProtectedRoute>} />
      <Route path="/settings/integrations/webhooks" element={<ProtectedRoute><WebhookManagementPage /></ProtectedRoute>} />
      <Route path="/roster/insights" element={<ProtectedRoute><RosterInsightsPage /></ProtectedRoute>} />
      <Route path="/roster/bulk-assign" element={<ProtectedRoute><BulkAssignShiftsPage /></ProtectedRoute>} />

      {/* Teams */}
      <Route path="/teams" element={<ProtectedRoute requireAdmin><TeamsListPage /></ProtectedRoute>} />
      <Route path="/teams/new" element={<ProtectedRoute requireAdmin><TeamFormPage /></ProtectedRoute>} />
      <Route path="/teams/:teamId" element={<ProtectedRoute requireAdmin><TeamDetailPage /></ProtectedRoute>} />
      <Route path="/teams/:teamId/edit" element={<ProtectedRoute requireAdmin><TeamFormPage /></ProtectedRoute>} />

      {/* Accommodation Extended */}
      <Route path="/accommodation/dashboard" element={<ProtectedRoute><AccommodationDashboardPage /></ProtectedRoute>} />
      {/* #12/#13: modules removed — residents and visitor logs live on each house. "/accommodation" is not a route
          (it fell through to /login), so send old links/bookmarks to Properties / Houses. */}
      <Route path="/accommodation/placements" element={<Navigate to="/accommodation/houses" replace />} />
      <Route path="/accommodation/visitors" element={<Navigate to="/accommodation/houses" replace />} />
      <Route path="/engagement/appointments" element={<ProtectedRoute><AppointmentsPage /></ProtectedRoute>} />
      <Route path="/engagement/messages" element={<ProtectedRoute><CommunicationCentrePage /></ProtectedRoute>} />
      <Route path="/engagement/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
      <Route path="/engagement/announcements" element={<ProtectedRoute><AnnouncementsPage /></ProtectedRoute>} />
      <Route path="/admin/security" element={<ProtectedRoute requireAdmin><SecurityDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/security/alerts" element={<ProtectedRoute requireAdmin><SecurityAlertsPage /></ProtectedRoute>} />
      <Route path="/admin/system" element={<ProtectedRoute requireAdmin><SystemAdminPage /></ProtectedRoute>} />
      <Route path="/admin/settings/branding" element={<ProtectedRoute requirePlatformAdmin requirePermission="org.manage_settings"><BrandingSettingsPage /></ProtectedRoute>} />   {/* platform admin only; backend require_platform_global */}
      <Route path="/admin/settings/feature-flags" element={<ProtectedRoute requirePlatformAdmin><FeatureFlagsPage /></ProtectedRoute>} />
      <Route path="/admin/allocations/parity-audit" element={<ProtectedRoute requirePlatformAdmin><ParityAuditPage /></ProtectedRoute>} />
      <Route path="/admin/allocations/live-governance" element={<ProtectedRoute requirePlatformAdmin><LiveSplitBillingGovernancePage /></ProtectedRoute>} />
      <Route path="/admin/allocations/command-center" element={<ProtectedRoute requirePlatformAdmin><ActivationCommandCenterPage /></ProtectedRoute>} />
      <Route path="/admin/settings/ratio-rules" element={<ProtectedRoute requireAdmin allowRoles={['Admin']}><RatioRulesPage /></ProtectedRoute>} />
      <Route path="/admin/settings/sleepover" element={<ProtectedRoute requireAdmin allowRoles={['Admin']}><SleepoverSettingsPage /></ProtectedRoute>} />
      <Route path="/admin/sleepover/estimate-comparison" element={<ProtectedRoute requireAdmin allowRoles={['Admin']}><SleepoverEstimateComparisonPage /></ProtectedRoute>} />
      <Route path="/admin/group-sessions" element={<ProtectedRoute requireAdmin><GroupSessionsPage /></ProtectedRoute>} />
      <Route path="/admin/group-sessions/:sessionId" element={<ProtectedRoute requireAdmin><GroupSessionDetailPage /></ProtectedRoute>} />
      <Route path="/admin/release-signoff" element={<ProtectedRoute requirePlatformAdmin><ReleaseSignoffPage /></ProtectedRoute>} />
      <Route path="/admin/reports" element={<ProtectedRoute requireAdmin><ReportsPage /></ProtectedRoute>} />
      <Route path="/admin/digest" element={<ProtectedRoute requireAdmin><DigestPage /></ProtectedRoute>} />
      <Route path="/admin/rollout" element={<ProtectedRoute requirePlatformAdmin><RolloutDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/smart-alerts" element={<ProtectedRoute requirePlatformAdmin><SmartAlertsPage /></ProtectedRoute>} />
      <Route path="/admin/smart-alerts/:id" element={<ProtectedRoute requirePlatformAdmin><SmartAlertDetailPage /></ProtectedRoute>} />
      <Route path="/admin/predictive-staffing" element={<ProtectedRoute requirePlatformAdmin><PredictiveStaffingPage /></ProtectedRoute>} />
      <Route path="/admin/revenue" element={<ProtectedRoute requirePlatformAdmin><RevenueOptimizationPage /></ProtectedRoute>} />
      <Route path="/admin/compliance" element={<ProtectedRoute requirePlatformAdmin><ComplianceAuditPage /></ProtectedRoute>} />
      <Route path="/admin/audit-reporting" element={<ProtectedRoute requireAdmin><AuditReportingPage /></ProtectedRoute>} />
      <Route path="/admin/soft-launch" element={<ProtectedRoute requirePlatformAdmin><SoftLaunchPage /></ProtectedRoute>} />
      {/* Day 25: Client Onboarding */}
      <Route path="/admin/onboarding" element={<ProtectedRoute requirePlatformAdmin><OnboardingDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/onboarding/new" element={<ProtectedRoute requirePlatformAdmin><OnboardingWizardPage /></ProtectedRoute>} />
      <Route path="/admin/onboarding/:id" element={<ProtectedRoute requirePlatformAdmin><OnboardingDetailPage /></ProtectedRoute>} />
      <Route path="/admin/onboarding/:id/import" element={<ProtectedRoute requirePlatformAdmin><OnboardingImportPage /></ProtectedRoute>} />
      <Route path="/admin/onboarding/:id/review" element={<ProtectedRoute requirePlatformAdmin><OnboardingReviewPage /></ProtectedRoute>} />
      <Route path="/hr/document-review" element={<ProtectedRoute><DocumentReviewDashboardPage /></ProtectedRoute>} />

      {/* Form Builder */}
      <Route path="/forms/dashboard" element={<ProtectedRoute><FormsDashboardPage /></ProtectedRoute>} />
      <Route path="/forms/templates" element={<ProtectedRoute><FormTemplatesPage /></ProtectedRoute>} />
      <Route path="/forms/templates/new" element={<ProtectedRoute><FormTemplateBuilderPage /></ProtectedRoute>} />
      <Route path="/forms/templates/:templateId" element={<ProtectedRoute><FormTemplateDetailPage /></ProtectedRoute>} />
      <Route path="/forms/templates/:templateId/edit" element={<ProtectedRoute><FormTemplateBuilderPage /></ProtectedRoute>} />
      <Route path="/forms/library" element={<ProtectedRoute requireAccess={canBrowseLibrary}><FormLibraryPage /></ProtectedRoute>} />
      <Route path="/forms/library/packs" element={<ProtectedRoute requirePlatformAdmin requireAccess={canAdminLibrary}><FormLibraryPacksPage /></ProtectedRoute>} />
      <Route path="/forms/library/:libraryId" element={<ProtectedRoute requireAccess={canBrowseLibrary}><FormLibraryItemPage /></ProtectedRoute>} />
      <Route path="/audit/registers" element={<ProtectedRoute requireAccess={canViewRegisters}><AuditRegistersHubPage /></ProtectedRoute>} />
      <Route path="/audit/registers/:registerType/print" element={<ProtectedRoute requireAccess={canViewRegisters}><AuditRegisterPrintPage /></ProtectedRoute>} />
      <Route path="/audit/registers/:registerType" element={<ProtectedRoute requireAccess={canViewRegisters}><AuditRegisterPage /></ProtectedRoute>} />
      <Route path="/audit/readiness" element={<ProtectedRoute requireAccess={canViewReadiness}><ReadinessOverviewPage /></ProtectedRoute>} />
      <Route path="/audit/readiness/outcomes" element={<ProtectedRoute requireAccess={canViewReadiness}><ReadinessOutcomesPage mode="outcomes" /></ProtectedRoute>} />
      <Route path="/audit/readiness/gaps" element={<ProtectedRoute requireAccess={canViewReadiness}><ReadinessOutcomesPage mode="gaps" /></ProtectedRoute>} />
      <Route path="/audit/readiness/review-due" element={<ProtectedRoute requireAccess={canViewReadiness}><ReadinessOutcomesPage mode="review-due" /></ProtectedRoute>} />
      <Route path="/audit/readiness/evidence-index" element={<ProtectedRoute requireAccess={canViewReadiness}><ReadinessEvidenceIndexPage /></ProtectedRoute>} />
      <Route path="/audit/readiness/bundle" element={<ProtectedRoute requireAccess={canExportReadiness}><EvidenceBundlePage /></ProtectedRoute>} />
      <Route path="/audit/readiness/outcomes/:outcomeId" element={<ProtectedRoute requireAccess={canViewReadiness}><ReadinessOutcomePage /></ProtectedRoute>} />
      <Route path="/forms/assignments" element={<ProtectedRoute><FormAssignmentsPage /></ProtectedRoute>} />
      <Route path="/forms/assignments/:assignmentId/fill" element={<ProtectedRoute><FormFillPage /></ProtectedRoute>} />
      <Route path="/forms/assignments/:assignmentId/print" element={<ProtectedRoute><FormPrintPage /></ProtectedRoute>} />

      {/* Redirects */}
      <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
      <Route path="/staff" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <QueryProvider>
        <AuthProvider>
          <BrandingProvider>
          <PortalAuthProvider>
            <AppDialogProvider>
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </AppDialogProvider>
          </PortalAuthProvider>
          </BrandingProvider>
        </AuthProvider>
      </QueryProvider>
    </BrowserRouter>
  );
}

export default App;
